// ChapterPill — the mobile scene indicator ("‹ 3 / 7 ›") that
// replaces the right-rail dot nav when the viewport drops below
// 861px. The pill sits flush above the safe area, with prev / next
// arrow buttons that are 44x44 touch targets. Active state is read
// from window.__planetSceneIndex.

import { useCallback, useEffect, useState } from 'react';
import { SCENES } from './planetSections';
import { onChapterNavClick } from '../utils/analytics';

function scrollToScene(i: number) {
  if (typeof window === 'undefined') return;
  const s = SCENES[i];
  if (!s) return;
  const max = document.documentElement.scrollHeight - window.innerHeight;
  if (max <= 0) return;
  const y = (s.start + s.end) / 2;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  window.scrollTo({
    top: y * max,
    behavior: reduced ? 'auto' : 'smooth',
  });
}

export function ChapterPill() {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    let last = window.__planetSceneIndex ?? 0;
    setIdx(last);
    const id = window.setInterval(() => {
      const cur = window.__planetSceneIndex ?? 0;
      if (cur !== last) {
        last = cur;
        setIdx(cur);
      }
    }, 200);
    return () => window.clearInterval(id);
  }, []);

  const prev = useCallback(() => {
    const next = Math.max(0, idx - 1);
    scrollToScene(next);
    onChapterNavClick(SCENES[next]?.key ?? '');
  }, [idx]);

  const next = useCallback(() => {
    const n = Math.min(SCENES.length - 1, idx + 1);
    scrollToScene(n);
    onChapterNavClick(SCENES[n]?.key ?? '');
  }, [idx]);

  const display = (idx + 1).toString().padStart(1, ' ');
  const total = SCENES.length.toString();

  return (
    <div className="chapter-pill" role="group" aria-label="Scene navigation">
      <button
        type="button"
        className="chapter-pill__btn"
        onClick={prev}
        disabled={idx <= 0}
        aria-label="Previous scene"
      >
        ‹
      </button>
      <span
        className="chapter-pill__index"
        aria-live="polite"
        aria-atomic="true"
      >
        {display} / {total}
      </span>
      <button
        type="button"
        className="chapter-pill__btn"
        onClick={next}
        disabled={idx >= SCENES.length - 1}
        aria-label="Next scene"
      >
        ›
      </button>
    </div>
  );
}

export default ChapterPill;
